/**
 * 新建一季 —— 项目 → 周期 → 计划书。
 *
 * 这三级缺一级，行组就没处挂：行组挂计划书，计划书挂周期与项目。
 * 之前管理端完全没有这条路，只能靠导入脚本开季，所以从零起一个新季度做不了。
 *
 * 设计成一个抽屉里三段，而不是三个页面：
 * 开季是一次性动作，用户脑子里是「我要开 2026 冬这一季」，
 * 不是「我要建一个项目，再建一个周期，再建一个计划书」。
 *
 * 已有项目时可以只建周期 + 计划书（往同一个项目里加一季）。
 */
import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Drawer, Form, Input, Radio, Select, Space, Typography, message } from 'antd'
import {
  fetchNextProjectCode,
  fetchStations,
  openSeason,
} from '@/services/seasonApi'
import { useSeason } from '@/context/SeasonContext'
import { describeError } from '@/services/errors'

const { Text } = Typography

/** 季次只有夏、冬两种（01=夏 02=冬），编码 6 位 YYYYSS。 */
const SEASONS = [
  { value: 'summer', label: '夏季', ord: '01' },
  { value: 'winter', label: '冬季', ord: '02' },
]

interface Props {
  open: boolean
  onClose: () => void
}

/** 3 位内部码：年后两位 + 季序（261 = 2026 夏）。行号前缀用它。 */
function shortCode(year: number, ord: string): string {
  return `${String(year).slice(-2)}${Number(ord)}`
}

export default function NewSeasonDrawer({ open, onClose }: Props) {
  const season = useSeason()
  const [form] = Form.useForm()
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [stations, setStations] = useState<Array<{ id: string; station_name: string }>>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const hasProjects = season.projects.length > 0

  useEffect(() => {
    if (!open) return
    setError('')
    setMode(hasProjects ? 'existing' : 'new')
    void fetchStations()
      .then((res) => setStations(res.items ?? []))
      .catch(() => setStations([]))
    const now = new Date()
    form.setFieldsValue({
      project_id: season.project?.id,
      crop_code: 'V16',
      color_hex: '#1F6F52',
      year: now.getFullYear(),
      season_type: now.getMonth() >= 9 || now.getMonth() <= 2 ? 'winter' : 'summer',
    })
  }, [open, hasProjects, season.project?.id, form])

  const year = Form.useWatch('year', form) as number | undefined
  const seasonType = Form.useWatch('season_type', form) as string | undefined
  const stationId = Form.useWatch('station_id', form) as string | undefined
  const cropCode = Form.useWatch('crop_code', form) as string | undefined

  const ord = useMemo(
    () => SEASONS.find((s) => s.value === seasonType)?.ord ?? '01',
    [seasonType],
  )
  const selectedProjectId = Form.useWatch('project_id', form) as string | undefined
  const projectStation = useMemo(
    () =>
      mode === 'existing'
        ? season.projects.find((p) => p.id === selectedProjectId)?.station_id
        : undefined,
    [mode, selectedProjectId, season.projects],
  )
  const cycleCode = year ? shortCode(year, ord) : ''
  const sixDigit = year ? `${year}${ord}` : ''

  // 新建项目时自动取下一个项目编号
  useEffect(() => {
    if (!open || mode !== 'new' || !stationId || !cropCode) return
    void fetchNextProjectCode({ crop_code: cropCode, station_id: stationId, year })
      .then((res) => form.setFieldValue('project_code', res.code))
      .catch(() => form.setFieldValue('project_code', undefined))
  }, [open, mode, stationId, cropCode, year, form])

  const submit = async () => {
    setError('')
    let values
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    setBusy(true)
    try {
      // 一个请求建齐三级。后端在一个事务里做，中途失败整体回滚 ——
      // 前端不需要再自己串三步、也不需要处理「周期建了但计划书没建」。
      const res = await openSeason(
        mode === 'new'
          ? {
              project_name: values.project_name,
              project_code: values.project_code || undefined,
              crop_code: values.crop_code,
              color_hex: values.color_hex,
              station_id: values.station_id,
              year: Number(values.year),
              season_type: values.season_type,
              plan_name: values.plan_name,
              breeding_objective: values.breeding_objective,
            }
          : {
              project_id: values.project_id,
              // 项目上没设实验站时表单会让用户补选
              station_id: values.station_id || undefined,
              year: Number(values.year),
              season_type: values.season_type,
              plan_name: values.plan_name,
              breeding_objective: values.breeding_objective,
            },
      )

      // 建完直接切到新季，用户接着就能去布置台排行
      season.reload()
      season.setProject(res.project_id)
      season.setCycle(res.cycle_id)
      season.setPlan(res.plan_id)

      if (res.reused.length > 0) {
        message.info(`这一季已存在，已切换过去（复用 ${res.reused.join('、')}）`)
      } else {
        message.success(`已开季 ${res.cycle_name} · 行号前缀 ${res.row_prefix}`)
      }
      form.resetFields()
      onClose()
    } catch (err) {
      setError(describeError(err, '开季失败'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Drawer
      title="新建一季"
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={busy} onClick={() => void submit()}>
            建好并切到这一季
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} closable />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="一季 = 项目 + 周期 + 计划书"
        description="行组挂在计划书上。三级建齐后才能去布置台排行。"
      />

      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item label="项目">
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="existing" disabled={!hasProjects}>
              用已有项目
            </Radio.Button>
            <Radio.Button value="new">新建项目</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {mode === 'existing' ? (
          <>
            <Form.Item
              name="project_id"
              label="选哪个项目"
              rules={[{ required: true, message: '请选择项目' }]}
            >
              <Select
                placeholder="选择项目"
                options={season.projects.map((p) => ({ value: p.id, label: p.project_name }))}
              />
            </Form.Item>
            {/* 项目上没设实验站时必须在这里补 —— 计划书的 station_id 是 NOT NULL。
                导入进来的老项目普遍没有这个字段。 */}
            {!projectStation && (
              <Form.Item
                name="station_id"
                label="实验站"
                rules={[{ required: true, message: '这个项目没有实验站，请选一个' }]}
                extra="所选项目未设实验站，计划书需要它"
              >
                <Select
                  placeholder="选择实验站"
                  options={stations.map((s) => ({ value: s.id, label: s.station_name }))}
                />
              </Form.Item>
            )}
          </>
        ) : (
          <>
            <Form.Item
              name="project_name"
              label="项目名称"
              rules={[{ required: true, message: '请填项目名称' }]}
            >
              <Input placeholder="如 向日葵转育" />
            </Form.Item>
            <Space style={{ display: 'flex' }} size={12}>
              <Form.Item
                name="crop_code"
                label="作物"
                rules={[{ required: true }]}
                style={{ flex: 1 }}
              >
                <Select options={[{ value: 'V16', label: '向日葵 (V16)' }]} />
              </Form.Item>
              <Form.Item
                name="station_id"
                label="实验站"
                rules={[{ required: true, message: '请选择实验站' }]}
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="选择实验站"
                  options={stations.map((s) => ({ value: s.id, label: s.station_name }))}
                />
              </Form.Item>
            </Space>
            <Form.Item name="project_code" label="项目编号" extra="留空按规则自动生成">
              <Input placeholder="自动生成" />
            </Form.Item>
            <Form.Item name="color_hex" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        <Space style={{ display: 'flex' }} size={12}>
          <Form.Item
            name="year"
            label="年份"
            rules={[{ required: true, message: '请填年份' }]}
            style={{ flex: 1 }}
          >
            <Input type="number" placeholder="2026" />
          </Form.Item>
          <Form.Item
            name="season_type"
            label="季次"
            rules={[{ required: true }]}
            style={{ flex: 1 }}
          >
            <Select options={SEASONS.map((s) => ({ value: s.value, label: s.label }))} />
          </Form.Item>
        </Space>

        {cycleCode && (
          <Alert
            type="success"
            style={{ marginBottom: 16 }}
            message={
              <Text>
                季次编码 <Text code>{sixDigit}</Text>，行号前缀{' '}
                <Text code>{cycleCode}</Text>（如 <Text code>{cycleCode}-T1</Text>）
              </Text>
            }
          />
        )}

        <Form.Item
          name="plan_name"
          label="计划书名称"
          rules={[{ required: true, message: '请填计划书名称' }]}
        >
          <Input placeholder={year ? `${year}${ord === '01' ? '夏' : '冬'}季计划书` : '计划书名称'} />
        </Form.Item>

        <Form.Item
          name="breeding_objective"
          label="育种目标"
          rules={[{ required: true, message: '请填育种目标 —— 后端要求必填' }]}
        >
          <Input.TextArea rows={2} placeholder="如 转育、鉴定、繁种" />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
